// content-dom.service.ts
import { Injectable } from '@angular/core';
import { BlockTypeEnum } from '@app/core/enums';
import { BlockContent } from '../pages/models/content.model';
import { ContentBlockService } from './content-block.service';

/**
 * Serwis odpowiedzialny za integrację struktur danych z elementami DOM
 * Obsługuje parsowanie i aktualizację elementów HTML oraz synchronizację ze strukturami bloków
 */
@Injectable({
  providedIn: 'root',
})
export class ContentDomService {
  private lang = 'pl';

  constructor(
    private blockService: ContentBlockService,
  ) {
    this.lang = localStorage.getItem('language') || 'pl';
  }

  /**
   * Główna metoda przetwarzająca element HTML i aktualizująca powiązane struktury danych
   * @param element - Element DOM do analizy
   * @remarks Automatycznie wykrywa typ bloku i uruchamia odpowiednią logikę aktualizacji
   * @throws Błędy parsowania przy nieprawidłowej strukturze elementu
   */
  processHtmlElement(element: HTMLElement): void {
    console.log('[ContentDomService->processHtmlElement] element', element);
    try {
      const blockId = this.extractIdentifiers(element);
      console.log('[ContentDomService->processHtmlElement] blockId', blockId);
      const blockToEdit = this.blockService.getBlockByIdFromStructure(blockId);
      console.log('[ContentDomService->processHtmlElement] blockToEdit', blockToEdit);

      if (!blockToEdit) {
        console.error('Nie znaleziono bloku o identyfikatorze:', blockId);
        return;
      }

      switch (blockToEdit.type) {
        case BlockTypeEnum.UNIVERSAL_TYPE:
          this.updateStructuresUNIVERSAL_TYPE(blockToEdit, element);
          break;
        default:
          console.log('Nieobsługiwany typ bloku:', blockToEdit.type);
      }
    } catch (error) {
      console.error('Błąd przetwarzania elementu:', error);
    }
  }

  /**
   * Ekstrahuje identyfikatory bloków z hierarchii klas elementu DOM
   * @param element - Element startowy do analizy
   * @returns Identyfikator bloku w formacie string
   * @throws Error jeśli nie znaleziono odpowiedniej klasy
   * @remarks Przeszukuje hierarchię rodziców w poszukiwaniu klasy 'block_'
   */
  extractIdentifiers(element: HTMLElement): string {
    let currentElement: Element | null = element;

    while (currentElement) {
      const classList = Array.from(currentElement.classList);
      const blockClass = classList.find(c => c.startsWith('block_'));

      if (blockClass) {
        return blockClass;
      }
      currentElement = currentElement.parentElement;
    }

    throw new Error('Nie znaleziono klasy block_ w hierarchii DOM');
  }

  /**
   * Aktualizuje strukturę uniwersalnego bloku treści na podstawie elementu DOM
   * @param block - Obiekt bloku do aktualizacji
   * @param element - Odpowiadający element DOM
   * @remarks Synchronizuje zawartość HTML, tłumaczenia i metadane bloku
   */
  private updateStructuresUNIVERSAL_TYPE(
    block: BlockContent,
    element: HTMLElement,
  ): void {
    console.log('[ContentDomService->updateStructuresUNIVERSAL_TYPE]');
    element = this.removeEmptyChildren(element);

    // Pobranie zawartości z DOM
    const domContent = element?.innerHTML || '';
    block.content = domContent;

    // Aktualizacja tłumaczeń
    if (Array.isArray(block.translations) && block.translations.length > 0) {
      const translation = block.translations.find(t => t.locale === this.lang);
      if (translation?.content?.trim()) {
        translation.content = domContent;
      }
    }

    // Aktualizacja przetłumaczalnych pól
    if (block.translated_fields) {
      if (!block.translated_fields[this.lang]) {
        block.translated_fields[this.lang] = {
          content: '',
          name: 'no-name',
          alt: null,
          locale: this.lang,
        };
      }
      block.translated_fields[this.lang].content = domContent;
    }

    // Zapis zmodyfikowanego bloku
    this.blockService.replaceBlockInStructure(block.u_id, block);
  }

  /**
   * Usuwa puste elementy potomne z struktury DOM
   * @param element - Element główny do czyszczenia
   * @returns Oczyszczony element DOM
   * @remarks Usuwa elementy bez treści tekstowej lub komentarzy
   */
  removeEmptyChildren(element: HTMLElement): HTMLElement {
    if (!element) return element;

    const children = Array.from(element.children);

    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i] as HTMLElement;

      if (
        !child.textContent?.trim() &&
        !child.innerHTML.replace(/<!--.*?-->/g, '').trim()
      ) {
        element.removeChild(child);
      }
    }

    return element;
  }
}
